<template>
  <div>
    <v-navigation-drawer
      v-if="this.configuration != null"
      width="420px"
      fixed
      right
      temporary
      v-model="drawer"
      class="bg-white h-100 d-grid-nav"
    >
      <v-row no-gutters>
        <v-col>
          <v-card class="elevation-0">
            <v-card-title :style="backgroundStarStyle">
              <v-icon size="35" class="black--text">show_chart</v-icon>
              <span class="headline black--text">Plot Configuration</span>
              <v-spacer></v-spacer>
              <exo-help-dialog
                :color="'black--text'"
                :margin="'m-0'"
                title="Simulation Plot Configuration"
              >
                The simulation can generate ephemerides with a user defined time
                step. The types of ephemerides and the time step are set in the
                mission configuration. These ephemerides can then be plotted to
                analyze the evolution during the simulation.
                <br />
                <br />
                <h5>Plot types</h5>
                <b>Line/Scatter chart </b>: 2 dimensional chart, single
                Y-axis.<br />
                <b>Multi axis </b>: several Y-axis to watch different
                ephemerides simultaneously.<br />
                <br />
                <h5>Customization</h5>
                <b>Display mode </b>: results can be displayed as lines, markers
                (not connected points) with or without text attached.<br />
                <b>Lines shape </b>:
                <ul style="margin: 0">
                  <li>linear : straight line between points</li>
                  <li>spline : interpolation for smooth rendering</li>
                </ul>
                <b>Axes </b>: name and unit <br />
                <b>Time filter </b>: only display a part of the whole simulation
                (timeline in days). <br />
                <b>Targets </b>: display horizontal lines matching specific
                values of ephemerides (min, max, initial, final). <br />
                <br />
              </exo-help-dialog>
            </v-card-title>
            <v-card-text class="p-0 pt-2">
              <v-form>
                <v-col class="px-3">
                  <v-text-field
                    horizontal
                    label="Title"
                    v-model="configuration.name"
                    maxlength="100"
                  >
                  </v-text-field>
                </v-col>
                <v-col class="px-3">
                  <v-select
                    :items="plotTypes"
                    label="Type"
                    v-model="configuration.type"
                    :menu-props="{ transition: 'scale-transition' }"
                  ></v-select>
                  <!--make functon-->
                  <v-select
                    v-if="isLineChart"
                    :items="scatterModesLineChart"
                    item-value="value"
                    item-text="label"
                    label="Display mode"
                    v-model="configuration.scatterMode"
                    :menu-props="{ transition: 'scale-transition' }"
                  >
                    <template slot="selection" slot-scope="{ item }">
                      {{ item.label }}
                    </template>
                    <template slot="item" slot-scope="{ item }">
                      {{ item.label }}
                    </template>
                  </v-select>
                  <v-select
                    v-if="isMultiAxis"
                    :items="scatterModesMultiAxis"
                    item-value="value"
                    item-text="label"
                    label="Display mode"
                    v-model="configuration.scatterMode"
                    :menu-props="{ transition: 'scale-transition' }"
                  >
                    <template slot="selection" slot-scope="{ item }">
                      {{ item.label }}
                    </template>
                    <template slot="item" slot-scope="{ item }">
                      {{ item.label }}
                    </template>
                  </v-select>
                  <v-select
                    v-if="displayScatterShape"
                    :items="scatterShapes"
                    label="Line shape"
                    item-value="value"
                    item-text="label"
                    v-model="configuration.scatterShape"
                    :menu-props="{ transition: 'scale-transition' }"
                  >
                    <template slot="selection" slot-scope="{ item }">
                      {{ item.label }}
                    </template>
                    <template slot="item" slot-scope="{ item }">
                      {{ item.label }}
                    </template>
                  </v-select>
                  <exo-select-field
                    v-if="displayScatterText"
                    :field="configuration.scatterText"
                    :root="_self"
                  />
                </v-col>
                <v-divider class="mt-0"></v-divider>
                <v-col class="px-3">
                  <exo-path-select-row
                    v-if="isMap"
                    :field="configuration.z"
                    fieldName="z"
                    :root="_self"
                  />
                  <exo-path-select-row
                    v-if="!isMultiAxis"
                    :field="configuration.y"
                    fieldName="y"
                    :root="_self"
                    :style="isLineChart ? 'margin-bottom: -14px;' : ''"
                  />
                  <div v-if="isMultiAxis">
                    <exo-path-select-row
                      v-for="(yn, index) in configuration.multiAxis"
                      :field="yn"
                      :fieldName="'y' + (index + 1)"
                      :key="yn.id"
                      :root="_self"
                    />
                    <exo-multi-axis-options
                      :root="_self"
                    ></exo-multi-axis-options>
                  </div>
                  <v-col cols="3" class="pt-3" v-if="isLineChart">
                    <v-btn
                      class="mt-3"
                      v-on:click="switchAxes"
                      :color="$vuetify.theme.dark ? '' : 'grey lighten-4'"
                    >
                      Switch axis
                    </v-btn>
                  </v-col>
                  <exo-path-select-row
                    fieldName="x"
                    :field="configuration.x"
                    :root="_self"
                  />
                </v-col>
                <v-col class="px-3">
                  <v-range-slider
                    v-if="
                      isDuration &&
                      Array.isArray(configuration.durationSliderValue) &&
                      configuration.durationSliderOptions.step != 0
                    "
                    v-model="configuration.durationSliderValue"
                    :min="configuration.durationSliderOptions.min"
                    :max="configuration.durationSliderOptions.max"
                    :step="configuration.durationSliderOptions.step"
                    label="Time filter"
                    color="rgb(51, 204, 255)"
                    thumb-label
                  >
                    <template v-slot:thumb-label="item"
                      ><span style="color: black">{{
                        item.value
                      }}</span></template
                    >
                  </v-range-slider>
                  <exo-target-config :root="_self"></exo-target-config>
                </v-col>
                <v-divider class="mb-0"></v-divider>
                <v-col class="px-3">
                  <v-row no-gutters>
                    <v-col cols="8">
                      <v-text-field
                        value="Display 0 on axis"
                        dense
                        solo
                        flat
                      ></v-text-field>
                    </v-col>
                    <v-col cols="2">
                      <v-checkbox
                        v-model="configuration.xzero"
                        id="inputxzero"
                        label="X"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="2">
                      <v-checkbox
                        v-model="configuration.yzero"
                        id="inputyzero"
                        label="Y"
                      ></v-checkbox>
                    </v-col>
                  </v-row>
                  <v-row no-gutters>
                    <v-col cols="8">
                      <v-text-field
                        value="Logarithmic axis"
                        dense
                        solo
                        flat
                      ></v-text-field>
                    </v-col>
                    <v-col cols="2">
                      <v-checkbox
                        class="p-3 m-0"
                        v-model="configuration.xlog"
                        id="inputxlog"
                        label="X"
                      ></v-checkbox>
                    </v-col>
                    <v-col cols="2">
                      <v-checkbox
                        class="p-3 m-0"
                        v-model="configuration.ylog"
                        id="inputylog"
                        label="Y"
                      ></v-checkbox>
                    </v-col>
                  </v-row>
                </v-col>
                <v-alert
                  show
                  variant="warning"
                  v-for="item in this.warningMessages"
                  v-bind:key="item.id"
                  >{{ item.message }}</v-alert
                >
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col>
          <div>
            <v-divider class="m-0"></v-divider>
            <v-container>
              <v-btn
                type="submit"
                @click="updatePlotData(false)"
                id="submitBtn"
                variant="primary"
                class="lg"
                >Draw</v-btn
              >
              <v-btn
                type="button"
                @click="cancelPlot()"
                id="cancelPlotBtn"
                variant="primary"
                class="lg"
                >Cancel plot</v-btn
              >
            </v-container>
          </div>
        </v-col>
      </v-row>
    </v-navigation-drawer>
  </div>
</template>

<script>
// import mixins dependencies
import loadPlotComponents from '../../components/Plot/plotComponents.js'
import plotInput from '../../mixins/plotInput.js'
import plotLayout from '../../mixins/plotLayout.js'
import plotData from '../../mixins/plotData.js'

import dataTools from '../../tools/data.js'
import Plotly from 'plotly.js-dist'
import { mapGetters } from 'vuex'
import 'vue-grid-layout'

loadPlotComponents()

export default {
  name: 'numerical-plot-config',

  mixins: [plotLayout, plotInput, plotData],

  mounted() {
    this.$root.$on('current-results-updated', this.currentResultsUpdatedHandler)
    this.$root.$on('set-plot-config', this.setPlotConfigHandler)
  },

  beforeDestroy() {
    this.hidePlotingArea()
    this.$root.$off(
      'current-results-updated',
      this.currentResultsUpdatedHandler
    )
    this.$root.$off('set-plot-config', this.setPlotConfigHandler)
  },

  computed: {
    ...mapGetters(['currentMission', 'currentResultsLog']),

    backgroundStarStyle() {
      return (
        'background: #3BE287'
      )
    },

    filteredTargetOptions() {
      return this.listAxisOptions(this.plotData).filter((el) => {
        return (
          dataTools.getSiUnit(el.value) ===
          dataTools.getSiUnit(this.configuration.y.path)
        )
      })
    },

    isWarningMessages() {
      return this.warningMessages && this.warningMessages.length > 0
    },

    isMap() {
      return (
        this.configuration.type === 'Contour map' ||
        this.configuration.type === 'Heat Map' ||
        this.configuration.type === 'Surface' ||
        this.configuration.type === '3D chart'
      )
    }
  },

  data() {
    // TODO MFI these data should be in plotLayout, plotInput, plotData
    return {
      drawer: false,
      plotTypes: ['Line/Scatter chart', 'Multi axis'],
      scatterModesLineChart: [
        { label: 'Lines + Markers', value: 'lines+markers' },
        { label: 'Lines', value: 'lines' },
        { label: 'Markers', value: 'markers' },
        { label: 'Markers + Text', value: 'markers+text' }
      ],
      scatterModesMultiAxis: [
        { label: 'Lines + Markers', value: 'lines+markers' },
        { label: 'Lines', value: 'lines' },
        { label: 'Markers', value: 'markers' }
      ],
      targetTypes: ['Initial', 'Final', 'Min', 'Max'],
      scatterShapes: [
        { label: 'Linear', value: 'linear' },
        { label: 'Spline', value: 'spline' }
      ],
      plotData: [],
      acceptPathUpdates: true,
      plotingArea: null,
      filteredResults: [],
      warningMessages: [],
      configuration: null,
      displayForm: true,
      fieldIndexes: undefined,
      type: 'SimulationResult'
    }
  },

  methods: {
    currentResultsUpdatedHandler() {
      if (
        this.currentMission != null &&
        this.currentMission.typeName === 'SimulationRequest'
      ) {
        this.updateCurrentMission()
      }
    },
    setPlotConfigHandler(config, plotingArea) {
      this.configuration = config
      this.plotingArea = plotingArea
      this.updateCurrentMission()
    },

    // Cant export there 3 next function because they are called from another component
    updateCurrentMission() {
      this.filteredResults = []
      this.colorIndex = 0
      this.plotData = []
      this.mission = this.currentMission
      if (
        this.mission != null &&
        this.currentResultsLog != null &&
        this.currentResultsLog.results != null &&
        this.currentResultsLog.results.ephemerides != null
      ) {
        this.plotData = dataTools.convertSimulationData(
          this.currentResultsLog.results.ephemerides,
          this.currentResultsLog.results.fieldIndexes
        )
        this.fieldIndexes = this.currentResultsLog.results.fieldIndexes
        if (this.mission != null) {
          /*
          let config = this.buildEmptyConfiguration()
          this.resetTreeConfig(config)
          this.configuration.name = this.mission.name
          */
          this.filteredResults = this.plotData
          if (this.configuration && this.configuration.x.value == null) {
            if (typeof this.configuration.y !== 'object') {
              const yaxisConfig = this.configuration.y
              this.configuration.y = {}
              this.configuration.y.path = yaxisConfig
              this.configuration.y.value = yaxisConfig
              this.treeUpdated(this.configuration.y, 'y')
            }
            this.configuration.x = {}
            this.configuration.x.path = ['simulationDuration']
            this.configuration.x.value = 'simulationDuration'
            this.treeUpdated(this.configuration.x, 'x')
            if (this.configuration.multiAxis.length !== 0) {
              const values = {
                x: this.getPlotValues(
                  this.configuration.x,
                  this.filteredResults
                )
              }
              for (const index in this.configuration.multiAxis) {
                const trace = this.initScatter(this.configuration)
                trace.x = values.x
                trace.y = this.getPlotValues(
                  this.configuration.multiAxis[index],
                  this.filteredResults
                )
                if (this.configuration.x.path[0] === 'simulationDuration') {
                  this.filterPlotDataFromTimeSlider(trace, this.configuration)
                }
                this.treeUpdated(
                  this.configuration.multiAxis[index],
                  'y'.concat(index.toString(10))
                )
                this.setMultiAxisLayout(
                  this.configuration,
                  index,
                  trace,
                  this.configuration.multiAxis
                )
                this.configuration.data.push(trace)
              }
            }
          }
          this.displayForm = true
          this.hidePlotingArea()
        } else {
          this.hidePlotingArea()
        }
      }
    },

    hidePlotingArea() {
      if (document.getElementById(this.plotingAreaName)) {
        try {
          Plotly.purge(this.plotingAreaName)
        } catch (e) {}
        this.configuration.isDrawn = false
      }
    },

    cancelPlot() {
      this.drawer = false
    },

    // Targets
    addNewTarget() {
      this.configuration.targets.push({
        treevalue: null,
        path: [],
        type: 'Final',
        color:
          this.getPlotlyColors()[
            ++this.colorIndex % this.getPlotlyColors().length
          ]
      })
    },

    pathTargetUpdated(parameter) {
      if (parameter.treevalue) {
        parameter.path = [parameter.treevalue.split('.').join('')]
        let fieldType = dataTools.getFieldTypeFromPath(
          parameter.path,
          this.type,
          null
        )
        parameter.unitOptions = dataTools.getAvailableUnits(fieldType)
        parameter.unit =
          dataTools.getDefaultGuiUnit(fieldType) === 'DHMS'
            ? 'days'
            : dataTools.getDefaultGuiUnit(fieldType)
        parameter.value = parameter.valueOptions
      }
    },

    removeTarget(index) {
      this.configuration.targets.splice(index, 1)
    },

    removeInvalidTargets() {
      for (let i = 0; i < this.configuration.targets.length; i++) {
        let path = this.configuration.targets[i].path
        if (path.length == 0) {
          this.removeTarget(i)
        }
      }
    },

    pathUpdated(parameter) {
      let fieldType = dataTools.getFieldTypeFromPath(
        parameter.path,
        this.type,
        null
      )
      parameter.unitOptions = dataTools.getAvailableUnits(fieldType)
      parameter.unit =
        dataTools.getDefaultGuiUnit(fieldType) === 'DHMS'
          ? 'days'
          : dataTools.getDefaultGuiUnit(fieldType)
      parameter.label = dataTools.getDataLabel(parameter.path)
    },

    pathFilterUpdated(parameter) {
      if (parameter.treevalue) {
        parameter.path = parameter.treevalue.split('.')
        let fieldType = dataTools.getFieldTypeFromPath(
          parameter.path,
          this.type,
          null
        )
        parameter.unitOptions = dataTools.getAvailableUnits(fieldType)
        parameter.unit =
          dataTools.getDefaultGuiUnit(fieldType) === 'DHMS'
            ? 'days'
            : dataTools.getDefaultGuiUnit(fieldType)
        parameter.valueOptions = dataTools
          .getUniqueValues(this.plotData, parameter.path)
          .map((val) => ({
            value: val,
            text: dataTools.convertFromSi(fieldType, val, parameter.unit)
          }))
        parameter.value = parameter.valueOptions
      }
    },

    // Draw clicked
    updatePlotData(fromLayoutSave) {
      let config = this.configuration == null ? {} : this.configuration
      this.colorIndex = 0
      config.data = []
      config.warningMessages = []
      this.initLayout(config)
      this.removeInvalidTargets()
      if (this.isMultiAxis) {
        this.addMultiAxisTrace()
      } else {
        if (this.isMap) {
          this.addMapTrace()
        } else if (this.isLineChart) {
          if (config.parameters.length === 0) {
            this.addTrace('', this.filteredResults)
          } else {
            this.addRecursiveTrace(0, this.filteredResults, [])
          }
        }
        config.layout.xaxis = {
          ...config.layout.xaxis,
          gridcolor: this.$vuetify.theme.dark ? '#656565' : '',
          linecolor: this.$vuetify.theme.dark ? '#656565' : '',
          zerolinecolor: this.$vuetify.theme.dark ? 'white' : '#121212',
          tickfont: {
            color: this.$vuetify.theme.dark ? 'white' : '#121212'
          }
        }
        config.layout.yaxis = {
          title: {
            text: config.y.label + dataTools.getUnitLabel(config.y.unit),
            font: {
              family: "'Roboto', sans-serif",
              size: 14,
              color: '#2a94de'
            }
          },
          showline: true,
          zeroline: true,
          rangemode: config.yzero ? 'tozero' : 'normal',
          hoverformat: '.' + dataTools.getNbToDisplay(config.x.unit) + 'f',
          tickfont: {
            color: '#2a94de'
          },
          gridcolor: this.$vuetify.theme.dark ? '#656565' : '',
          linecolor: this.$vuetify.theme.dark ? '#656565' : '',
          zerolinecolor: this.$vuetify.theme.dark ? 'white' : '#121212'
        }
        if (config.ylog) {
          config.layout.yaxis.type = 'log'
          config.layout.yaxis.autorange = true
        }
      }
      this.configuration.isDrawn = true
      this.drawer = false
      this.updatePlot(this.plotingArea, fromLayoutSave)
    },

    checkUncompleteConfiguration(values, results) {},

    addTrace(traceName, results) {
      let config = this.configuration
      let values = this.initScatter(config)
      if (traceName !== '') {
        values.name = traceName
      }
      values.x = this.getPlotValues(config.x, results)
      values.y = this.getPlotValues(config.y, results)
      if (this.configuration.x.path[0] === 'simulationDuration') {
        this.filterPlotDataFromTimeSlider(values, config)
      }
      this.setBasicLayoutY(config)
      this.setScatterValues(config, values)
      this.addTargetsTraces(config, values)
      this.checkUncompleteConfiguration(values, results)
      config.data.push(values)
    },

    addRecursiveTrace(parameterIndex, filteredResults, traceNames) {
      let config = this.configuration
      let parameter = config.parameters[parameterIndex]
      let fieldType = dataTools.getFieldTypeFromPath(parameter, this.type, null)

      let paramLabel = dataTools.getUnitLabel(parameter.unit)
      let uniqueVals = dataTools.getUniqueValues(
        filteredResults,
        parameter.path
      )
      for (let paramValue of uniqueVals) {
        let newTraceNames = JSON.parse(JSON.stringify(traceNames)) // copy values, not ref to not modify source
        newTraceNames.push(
          dataTools.convertFromSi(fieldType, paramValue, parameter.unit) +
            paramLabel
        )
        let currentResult = filteredResults.filter(
          (element) =>
            dataTools.getValue(element, parameter.path) === paramValue
        )
        if (parameterIndex < config.parameters.length - 1) {
          this.addRecursiveTrace(
            parameterIndex + 1,
            currentResult,
            newTraceNames
          )
        } else {
          // last parameter : add the trace
          this.addTrace(newTraceNames.join(' / '), currentResult)
        }
      }
    },

    getMeanValuesIfPossible(config, parameter, results, filteredFields) {
      let parameterGroupField = parameter.value.split('.')[0]
      let parameterSubField = parameter.value.split('.')[1]
        ? parameter.value.split('.')[1].toLowerCase()
        : 'regzerfb'
      let isGoodField = filteredFields.find((axeToMerge) => {
        let axeToMergeGroup = axeToMerge.split('.')[0]
        let axeToMergeSubField = axeToMerge.split('.')[1]
          ? axeToMerge.split('.')[1].toLowerCase()
          : 'athtyuk,'
        return (
          axeToMergeGroup === parameterGroupField &&
          (axeToMergeSubField === parameterSubField ||
            axeToMergeSubField.includes(parameterSubField) ||
            parameterSubField.includes(axeToMergeSubField))
        )
      })
      if (isGoodField) {
        if (parameter.value.includes('Mean')) {
          let potentialOscName = parameter.value.replace('Mean', '')
          let values = this.initScatter(config)
          values.x = this.getPlotValues(config.x, results)
          let yConfig = this.buidDefaultPlotParameter()
          yConfig.value = potentialOscName
          this.treeUpdated(yConfig, 'y')
          values.y = this.getPlotValues(yConfig, results)
          values.name = yConfig.label
          return values
        } else {
          let potentialMeanName = parameter.value.replace(
            parameterGroupField + '.',
            parameterGroupField + '.Mean'
          )
          let values = this.initScatter(config)
          values.x = this.getPlotValues(config.x, results)
          let yConfig = this.buidDefaultPlotParameter()
          yConfig.value = potentialMeanName
          this.treeUpdated(yConfig, 'y')
          values.y = this.getPlotValues(yConfig, results)
          values.name = yConfig.label
          return values
        }
      }
      return false
    },

    filterMeanOscSameFieldsToSameAxis(config) {
      let filteredFields = []
      config.multiAxisTmp.forEach((axis, axisIndex) => {
        if (axis != null && axis.value != null) {
          let splited = axis.value.split('.')
          let splitedSecondPart = splited[1] ? splited[1] : 'cetheyjer'
          let found = config.multiAxisTmp.find((el) => {
            if (el == null || el.value == null) {
              return false
            }
            let mainField = el.value.split('.')[0]
            let secondaryField = el.value.split('.')[1]
              ? el.value.split('.')[1].toLowerCase()
              : 'dzefyjyk'
            return (
              mainField === splited[0] &&
              secondaryField !== splitedSecondPart.toLowerCase() &&
              (secondaryField.includes(splitedSecondPart.toLowerCase()) ||
                splitedSecondPart.toLowerCase().includes(secondaryField))
            )
          })
          if (found) {
            config.multiAxisTmp.splice(axisIndex, 1)
            filteredFields.push(axis.value)
          }
        }
      })
      return filteredFields
    },

    addMultiAxisTrace() {
      let config = this.configuration
      let values = { x: this.getPlotValues(config.x, this.filteredResults) }
      this.addTargetsTraces(config, values)
      config.multiAxisTmp = JSON.parse(JSON.stringify(config.multiAxis))
      let filteredFields = this.filterMeanOscSameFieldsToSameAxis(config)
      for (let index in config.multiAxisTmp) {
        let yn = config.multiAxisTmp[index]
        if (yn != null && yn.value != null && yn.path !== []) {
          let trace = this.initScatter(config)
          trace.x = values.x
          trace.y = this.getPlotValues(yn, this.filteredResults)
          if (this.configuration.x.path[0] === 'simulationDuration') {
            this.filterPlotDataFromTimeSlider(trace, config)
          }
          this.setMultiAxisLayout(config, index, trace, config.multiAxisTmp)
          let additionalData = this.getMeanValuesIfPossible(
            config,
            yn,
            this.filteredResults,
            filteredFields
          )
          if (additionalData) {
            // Autorange if mean osc ploted on same axis
            let indexPlusOne = 1 + Number(index)
            let suffix = index > 0 ? '' + indexPlusOne : ''
            let axisName = 'yaxis' + suffix
            config.layout[axisName].autorange = true
            additionalData.yaxis = 'y' + suffix
            config.data.push(additionalData)
          }
          config.data.push(trace)
        }
      }
    },

    addMapTrace() {
      let config = this.configuration
      let currentResult = this.filteredResults
      let traceType = this.getMapType(this.configuration.type)
      let trace = this.setMapLayout(traceType, config)
      trace.x = this.getPlotValues(config.x, currentResult)
      trace.y = this.getPlotValues(config.y, currentResult)
      trace.z = this.getPlotValues(config.z, currentResult)
      if (traceType === 'surface') {
        config.layout.width = 1200
        config.layout.height = 1000
        this.setSurfaceData(trace)
      }
      this.setMapAxisTitles(config)
      config.data.push(trace)
    },

    addWarning(message) {
      if (
        this.warningMessages.filter((el) => el.message === message).length === 0
      ) {
        this.warningMessages.push({
          message: message,
          id: this.warningMessages.length
        })
      }
    }
  }
}
</script>

<style scoped>
.d-grid-nav {
  height: 100%;
  display: inline-grid;
}
.card-bottom {
  display: inline-block;
  align-self: flex-end;
}
</style>
